import * as React from 'react';

import { DrawerContext, Grid, ModalContext } from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';
import { AdditionalServicesAvailability } from '@notino/web-tracking';

import { ContentWrapper } from '@components';
import { DrawerProvider } from '@components/BottomSheet/DrawerProvider';
import ErrorBoundary from '@components/ErrorBoundary';
import { RenderInView } from '@components/RenderInView';
import { BrandLogo } from '@containers/ProductDetailContainer/ProductDetail/ProductImageGallery/BrandLogo';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import useFeatureSwitches from '@hooks/useFeatureSwitches';
import {
  TabSwitcherProvider,
  useTabSwitcher,
} from '@sharedComponents/TabSwitcher/context/TabSwitcherContext';

import {
  ProductDetailContextProvider,
  useProductDetailContext,
} from '../ProductDetailContext';

import { BlogArticles } from './BlogArticles';
import { Breadcrumb } from './Breadcrumb';
import { Header } from './Header';
import { HeadMeta } from './HeadMeta';
import { useDispatchProductsShowEvent } from './hooks/useDispatchProductsShowEvent';
import { useAddToLastVisited } from './hooks/useLastVisited/useAddToLastVisited';
import { useModifaceFns } from './hooks/useModiface';
import { useRegisterNotinoApi } from './hooks/useRegisterNotinoApi';
import { useTrackItemAndReload } from './hooks/useTrackItemAndReload';
import { useTrackLoyaltyStatus } from './hooks/useTrackLoyaltyStatus';
import { useTrackPageView } from './hooks/useTrackPageView';
import { useTrackProductUrlChange } from './hooks/useTrackProductUrlChange';
import { LastVisitedProducts } from './LastVisitedProducts';
import { TabIds } from './model';
import { PdFaq } from './PdFaq';
import { ProductAlternatives } from './ProductAlternatives';
import { ProductBaseInfo } from './ProductBaseInfo';
import { DeliveriesDisplayMode } from './ProductBaseInfo/DeliveryTime';
import { ProductImageGallery } from './ProductImageGallery';
import { ProductJsonLd } from './ProductJsonLd';
import { ProductPictogram } from './ProductPictogram';
import { ProductTabContent } from './ProductTabContent';
import MobileTabContentPlaceholder, {
  useMoveTabsToMobilePlaceholder,
} from './ProductTabContent/MobileTabContentPlaceholder';
import { TabsWithExpandableTextProvider } from './ProductTabContent/ProductTabContentProvider';
import { RoutineOrRelatedProducts } from './RoutineOrRelatedProducts';
import { SponsoredAdsProducts } from './SponsoredAdsProducts';
import { StickySidebar } from './StickySidebar';
import {
  GalleryWrapper,
  MainInfoWrapper,
  TopSection,
  ProductTabContentGrid,
  PDViewPort,
  ShowOnMobile,
  ShowOnDesktop,
  AbsoluteTabsWrapper,
} from './styled';
import { getVariantDescription, isDiorBrand } from './utils';
import { VariantName } from './VariantName';

interface IPDProps {
  PDView: GetCatalogProductViewQuery;
  rcKey: string;
  customRcKey: string | null;
  customVersionKey: string | null;
}
const tabsWithExpandableText = [TabIds.Description, TabIds.RichContent];

const ProductDetailComponent = ({
  PDView: {
    Settings: { imageDomainPath, deliveriesDisplayMode },
  },
  customVersionKey,
}: IPDProps) => {
  const { product, currentCurrency, currentLocale } = useProductDetailContext();
  const { reviewsOnServerIds } = useFeatureSwitches();

  const galleryWrapperRef = React.useRef<HTMLDivElement>(
    null
  ) as React.MutableRefObject<HTMLDivElement>;

  const { absoluteTabsWrapperRef, tabsPlaceholderRef, topSectionRef } =
    useMoveTabsToMobilePlaceholder();

  const {
    experiment_pd_sticky_atc_button,
    release_pd_sponsored_ads_products,
    killswitch_pd_faq,
  } = useFeatureFlags();

  const {
    engravingAvailable,
    tryItFirstAvailable,
    currentVariant: variant,
  } = useProductDetailContext();

  const { handleOpenModifaceModal, hasVariantModiface } = useModifaceFns(
    product,
    variant
  );

  useDispatchProductsShowEvent(product.variants, {
    section: 'product_detail',
  });
  useAddToLastVisited(product, variant);

  useTrackLoyaltyStatus();
  useTrackPageView(variant);
  useTrackItemAndReload();

  useRegisterNotinoApi(variant.productCode);

  useTrackProductUrlChange(product, variant);

  const hasModiface = hasVariantModiface(variant);

  React.useEffect(() => {
    if (window.location.hash === '#vto' && hasModiface) {
      handleOpenModifaceModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDior = isDiorBrand(product.brand?.id ?? '');

  useTabSwitcher(); // triggers required rerender for <MobileTabContentPlaceholder/> to calculate its height properly (without it the height was not calulated on some sold out products)

  const tabsComponent = (
    <AbsoluteTabsWrapper ref={absoluteTabsWrapperRef}>
      <ProductTabContentGrid fluid={true}>
        <TabsWithExpandableTextProvider
          tabsWithExpandableText={tabsWithExpandableText}
        >
          <ProductTabContent
            reviewsOnServerIds={reviewsOnServerIds}
            product={product}
            variant={variant}
            customVersionKey={customVersionKey ?? ''}
          />
        </TabsWithExpandableTextProvider>
      </ProductTabContentGrid>
    </AbsoluteTabsWrapper>
  );

  return (
    <>
      <ErrorBoundary>
        <HeadMeta product={product} variant={variant} />
      </ErrorBoundary>
      <ErrorBoundary>
        <PDViewPort
          // This attribute needs to be here due to exponea recommendations
          data-category={product.category}
          // This  attribute needs to be here due to deloitte
          data-product-code={variant.productCode}
        >
          <ProductJsonLd
            product={product}
            selectedVariant={variant}
            imageDomainPath={imageDomainPath}
          />

          <ContentWrapper
            isWider={true}
            isAddToCartSticky={!!experiment_pd_sticky_atc_button}
          >
            <Grid fluid={true}>
              <BrandLogo
                brandId={product.brand?.id ?? ''}
                brandUrl={product.brand?.url ?? ''}
              />

              <TopSection ref={topSectionRef}>
                <GalleryWrapper ref={galleryWrapperRef}>
                  <ShowOnMobile>
                    <Breadcrumb
                      items={(product.breadcrumb ?? []).map((b) => ({
                        ...b,
                        link: b.link ?? '',
                      }))}
                    />

                    <Header
                      reviewsId="productBaseInfo-mobile"
                      productId={product.webMasterId}
                      catalogId={product.catalogMasterId}
                      productCode={product.masterProductCode}
                      subName={product.subName}
                      brandInfo={product.brand}
                      variantName={variant.name ?? ''}
                      desc={getVariantDescription(variant, product)}
                      attributes={variant.attributes}
                      visualsOnly={true}
                    />
                  </ShowOnMobile>

                  <ProductImageGallery
                    key={variant.webId} // NOTE: reset gallery to first image when variant changes
                    media={variant.media}
                    domain={imageDomainPath}
                    hasModiface={hasModiface}
                    openModifaceModal={handleOpenModifaceModal}
                    pictogram={
                      <ProductPictogram attributes={variant.attributes} />
                    }
                  />

                  <ProductAlternatives showOn="desktop" />

                  {tabsComponent}
                </GalleryWrapper>

                <MainInfoWrapper>
                  <StickySidebar galleryWrapperRef={galleryWrapperRef}>
                    <ShowOnDesktop>
                      <Breadcrumb
                        items={(product.breadcrumb ?? []).map((b) => ({
                          ...b,
                          link: b.link ?? '',
                        }))}
                        renderJsonLd={false}
                      />

                      <Header
                        reviewsId="productBaseInfo"
                        productId={product.webMasterId}
                        catalogId={product.catalogMasterId}
                        productCode={product.masterProductCode}
                        subName={product.subName}
                        brandInfo={product.brand}
                        variantName={variant.name ?? ''}
                        desc={getVariantDescription(variant, product)}
                        attributes={variant.attributes}
                      />
                    </ShowOnDesktop>

                    <ProductBaseInfo
                      brand={product.brand?.name ?? ''}
                      selectedVariant={variant}
                      product={product}
                      variants={product.variants}
                      currency={currentCurrency as any}
                      locale={currentLocale as any}
                      imageDomainPath={imageDomainPath}
                      deliveriesDisplayMode={
                        deliveriesDisplayMode as DeliveriesDisplayMode
                      }
                      shadefinderAvailable={
                        product.features?.isShadeFinderAvailable ?? false
                      }
                      variantName={<VariantName variant={variant} />}
                      engravingAvailable={
                        engravingAvailable as AdditionalServicesAvailability
                      }
                      tryItFirstAvailable={
                        tryItFirstAvailable as AdditionalServicesAvailability
                      }
                    />
                  </StickySidebar>
                </MainInfoWrapper>

                <ProductAlternatives showOn="mobile" />
              </TopSection>
            </Grid>

            <MobileTabContentPlaceholder
              ref={tabsPlaceholderRef}
              tabsAbsoluteContainerEl={
                absoluteTabsWrapperRef.current as HTMLDivElement
              }
            />

            <ErrorBoundary>
              <RenderInView>
                <RoutineOrRelatedProducts
                  productCode={product.masterProductCode}
                  relatedProducts={product.related.map((id) => String(id))}
                />
              </RenderInView>
            </ErrorBoundary>

            {!isDior && (
              <ErrorBoundary>
                <RenderInView>
                  <LastVisitedProducts variants={product.variants} />
                </RenderInView>
              </ErrorBoundary>
            )}

            {!isDior && release_pd_sponsored_ads_products && (
              <ErrorBoundary>
                <RenderInView>
                  <SponsoredAdsProducts />
                </RenderInView>
              </ErrorBoundary>
            )}

            {killswitch_pd_faq && (
              <ErrorBoundary>
                <PdFaq />
              </ErrorBoundary>
            )}

            <ErrorBoundary>
              <RenderInView>
                <BlogArticles />
              </RenderInView>
            </ErrorBoundary>
          </ContentWrapper>
        </PDViewPort>
      </ErrorBoundary>
    </>
  );
};

export const ProductDetail = (props: IPDProps) => (
  <ProductDetailContextProvider {...props}>
    <TabSwitcherProvider>
      <ModalContext.Provider>
        <DrawerContext.Provider>
          <DrawerProvider>
            <ProductDetailComponent {...props} />
          </DrawerProvider>
        </DrawerContext.Provider>
      </ModalContext.Provider>
    </TabSwitcherProvider>
  </ProductDetailContextProvider>
);
